import filesize from 'filesize';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import {dateFormatFR} from '../../../../utils';
import DataTable from '../../../../common/components/Datatable/Datatable';
import {FILTER_EXPRESSION_OPERATOR, FILTER_OPERATOR, ORDER_DIRECTION} from '../../../../common/constants';
import {GRAPHQL_QUERY_RECEIPTS} from '../../../../common/queries/receipts';
import DateRangePicker from '../../../../common/components/Dates/DateRangePicker';
import TicketRowAction from '../../../ticket/components/TicketRowActionAdmin';

const MargedDiv = styled.div`
  margin: 5px;
`;

const columns = t => [
  {
    Header: '#',
    accessor: 'id',
    sortable: true,
  },
  {
    Header: t('dataTable.merchantBusinessName'),
    accessor: 'merchant.businessName',
    sortable: true,
  },
  {
    Header: t('brand'),
    accessor: 'brand.name',
    sortable: true,
  },
  {
    Header: t('dataTable.email'),
    accessor: 'user.email',
    sortable: true,
  },
  {
    Header: t('dataTable.mobilePhone'),
    accessor: 'user.mobilePhone',
    sortable: true,
  },
  {
    Header: t('dataTable.creation'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'createdAt',
    sortable: true,
  },
  {
    Header: t('dataTable.fileSize'),
    Cell: ({cell}) => filesize(cell.value),
    accessor: 'file.size',
    sortable: true,
  },
  {
    Header: t('dataTable.detail'),
    Cell: ({cell}) => <TicketRowAction receipt={cell.value}/>,
    accessor: row => row,
  },
];

const createFilters = (search, merchantId, startDate, endDate) => {
  const andFilters = [];

  const startObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.GreaterThan,
    value: startDate,
  };

  const endObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.LessThan,
    value: endDate,
  };

  const merchantFilterObject = {
    field: 'receipt.merchant.id',
    operator: FILTER_OPERATOR.Equal,
    value: merchantId,
  };

  if (startDate) {
    andFilters.push(startObject);
  }

  if (endDate) {
    andFilters.push(endObject);
  }

  andFilters.push(merchantFilterObject);

  const expressions = [{
    operator: FILTER_EXPRESSION_OPERATOR.And,
    filters: andFilters,
    expressions: {
      operator: FILTER_EXPRESSION_OPERATOR.Or,
      filters: [{
        field: 'receipt.id',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }, {
        field: 'user.email',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      },{
        field: 'merchant.businessName',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }, {
        field: 'brand.name',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }],
    },
  }];

  return {
    expressions,
    relations: ['user', 'brand', 'merchant', 'file'],
  };
};


const MerchantTicketsDetails = ({t, merchantId}) => {
  const [{startDate, endDate}, setDateFilter] = useState({});
  const [search, setSearch] = useState('');

  const additionalFilters = (
    <MargedDiv>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDatesChange={({startDate, endDate}) => setDateFilter({startDate, endDate})}/>
    </MargedDiv>
  );

  return (
    <DataTable
      query={GRAPHQL_QUERY_RECEIPTS}
      columns={columns(t)}
      keyResult="receipts"
      defaultSort={{field: 'createdAt', direction: ORDER_DIRECTION.DESC}}
      onSearch={value => setSearch(value)}
      advancedFilters={createFilters(search, merchantId, startDate, endDate)}
      additionalFilters={additionalFilters}
    />
  );
};

MerchantTicketsDetails.propTypes = {
  t: PropTypes.func.isRequired,
  merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default withTranslation('ticket')(MerchantTicketsDetails);
